import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as styleLink from '../../../styles/communs/polylink.module.scss';
import * as toolSearchStyle from '../../../styles/custom/toolSearchStyle.module.scss';
import PolyLink from '../PolyLink';
import IconContainer from '../IconContainer';
import { GatsbyImage } from 'gatsby-plugin-image';
import Category from '../filter/Category';
import clsx from 'clsx';

const ToolSearch = ({ elem }) => {
  const { config } = elem;
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({});

  const mergeElementData = () => {
    if (elem && elem.config?.tools && elem.imgs) {
      const cnTools = elem.config?.tools?.length;
      const cntImgs = elem.imgs?.length;

      for(let i = 0; i < cnTools && i < cntImgs; i++) {
        elem.config.tools[i].childImageSharp = elem.imgs[i]?.childImageSharp.gatsbyImageData;
      }
    }
  };

  mergeElementData();

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const removeSpecialCharacters = (str) => {
    return str
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-zA-Z0-9 ]/g, '') // Remove special characters
      .toLowerCase();
  };

  const handleFilterChange = useCallback((categoryId, optionId) => {
    setSelectedFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (newFilters[categoryId]?.includes(optionId)) {
        newFilters[categoryId] = newFilters[categoryId].filter((id) => id !== optionId);
      } else {
        newFilters[categoryId] = [...(newFilters[categoryId] || []), optionId];
      }
      return newFilters;
    });
  }, []);

  const filterTools = (tools) => {
    const normalizedSearchTerm = removeSpecialCharacters(searchTerm.toLowerCase());
    return tools.filter((tool) => {
      const normalizedToolName = removeSpecialCharacters(tool.nom.toLowerCase());
      const matchesSearchTerm = normalizedToolName.includes(normalizedSearchTerm);
      const matchesFilters = Object.keys(selectedFilters).every((categoryId) => {
        return selectedFilters[categoryId].every((optionId) => tool[optionId]);
      });
      return matchesSearchTerm && matchesFilters;
    });
  };

  const getFilteredOptions = (category) => {
    return category.options.filter((option) => {
      return config.tools.some((tool) => tool[option.id]);
    });
  };

  const getFilteredCategories = () => {
    return config.filter.categories.filter((category) => {
      const filteredOptions = getFilteredOptions(category);
      return filteredOptions.length > 0;
    });
  };

  const filteredTools = filterTools(config.tools);
  const filteredCategories = getFilteredCategories();

  return (
    <div className={toolSearchStyle.root}>
      <h2 className={toolSearchStyle.title}>
        {config.filter?.title}
      </h2>
      <div className={toolSearchStyle.encadre}>
        {config.filter?.search?.allowSearch && (
          <>
            <p className={toolSearchStyle.titleCategory}>
              {config.filter?.search?.title}
            </p>
            <div className={toolSearchStyle.searchContainer}>
              <IconContainer
                classname={toolSearchStyle.searchIcon}
                color="BLACK"
                icon="SEARCH"
                noHover
                size={15}
                variant="basic"
              />
              <input
                aria-label={t('toolsearch.title.search', 'Rechercher')}
                className={toolSearchStyle.searchInput}
                onChange={handleSearchChange}
                placeholder={config.filter?.search?.placeHolder || t('toolsearch.title.search', 'Rechercher')}
                type="text"
                value={searchTerm}
              />
            </div>
          </>
        )}
        {filteredCategories.map((category, index) => {
          const filteredOptions = getFilteredOptions(category);
          return (
            <div className={toolSearchStyle.category}
              key={category.id + index}
            >
              <p className={toolSearchStyle.titleCategory}>
                {category.label}
              </p>
              <div className={toolSearchStyle.optionContainer}>
                {filteredOptions.map((option) => (
                  <div
                    className={toolSearchStyle.option}
                    key={option.id}
                  >
                    <Category
                      ariaLabel={option.label}
                      clicked={!selectedFilters[category.id]?.includes(option.id) || false}
                      handleClick={() => handleFilterChange(category.id, option.id)}
                      label={option.label}
                      lang="fr"
                      underline={false}
                      variant="contained"
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className={toolSearchStyle.toolTilesContainer}>
        {filteredTools.map((tool) => (
          <PolyLink
            className={toolSearchStyle.toolTile}
            key={tool.id}
            style={{gridColumn: `span ${tool.column}`}}
            target={tool.newWindow ? '_blank' : '_self'}
            url={tool.url}
          >
            <GatsbyImage
              alt={tool.nom}
              className={toolSearchStyle.toolImage}
              image={tool.childImageSharp}
            />
            <div
              className={toolSearchStyle.toolContent}
            >
              <h3
                className={clsx(styleLink.withoutIcon, toolSearchStyle.toolTitle)}
              >
                {tool.nom}
              </h3>
              <p>
                {tool.sousTitre}
              </p>
            </div>
          </PolyLink>
        ))}
      </div>
      {filteredTools.length === 0 && (
        <p className={toolSearchStyle.noResults}>
          {t('toolsearch.title.no_results', 'Aucun résultat')}
        </p>
      )}
    </div>
  );
};

ToolSearch.propTypes = {
  elem: PropTypes.shape({
    imgs: PropTypes.arrayOf(
      PropTypes.shape({
        childImageSharp: PropTypes.shape({
          id: PropTypes.string.isRequired,
          gatsbyImageData: PropTypes.object.isRequired
        })
      }),
    ),
    config: PropTypes.shape({
      filter: PropTypes.shape({
        title: PropTypes.string,
        search: PropTypes.shape({
          allowSearch: PropTypes.bool,
          title: PropTypes.string,
          placeHolder: PropTypes.string,
        }),
        categories: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            options: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
              })
            ).isRequired,
          })
        ).isRequired,
      }).isRequired,
      tools: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          nom: PropTypes.string.isRequired,
          sousTitre: PropTypes.string.isRequired,
          description1: PropTypes.string.isRequired,
          description2: PropTypes.string.isRequired,
          childImageSharp: PropTypes.shape({
            id: PropTypes.string,
            gatsbyImageData: PropTypes.object
          }),
          url: PropTypes.string.isRequired,
          disponibiliteAchat: PropTypes.bool,
          disponibiliteLicenceIndividuelle: PropTypes.bool,
          disponibiliteLicenceCampus: PropTypes.bool,
          recolterDonnees: PropTypes.bool,
          analyserDonnees: PropTypes.bool,
          faireInference: PropTypes.bool,
          annoterDonnees: PropTypes.bool,
          traduireTexte: PropTypes.bool,
          faireBibliographie: PropTypes.bool,
          faireCartes: PropTypes.bool,
          faireTranscription: PropTypes.bool,
          donneesStructurees: PropTypes.bool,
          donneesTextuelles: PropTypes.bool,
          donneesImages: PropTypes.bool,
          donneesSonores: PropTypes.bool,
          donneesVideo: PropTypes.bool,
          interfaceGraphique: PropTypes.bool,
          interfaceProgrammatique: PropTypes.bool,
          serveurLibreOuLocal: PropTypes.bool,
          serveurUE: PropTypes.bool,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

export default ToolSearch;